import { useEffect, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, ChevronLeft, ChevronRight, Clock, Copy, Download, ExternalLink, Eye, FileText, Hash, Loader2, MessageSquare, Paperclip, Receipt, RotateCcw, Send, Stamp, X, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { BACKEND_BASE_URL } from '../config/backendConfig';
import { fetchDisputeDetail, fetchDnDocumentUrl, fetchDocumentUrl, runDisputeOpsAction } from '../utils/api';
import {
  brandColor, brandInitials, fmtDateTime, fmtFullINR, formatIssueType,
} from '../utils/disputeFormat';

const DOC_ICON = { POD: Stamp, INVOICE_COPY: Receipt, SUPPORT: FileText, PLATFORM_RESPONSE: Download, OTHER: FileText };
const DOC_LABEL = { POD: 'Signed POD', INVOICE_COPY: 'Invoice copy', SUPPORT: 'Supporting document', PLATFORM_RESPONSE: 'From platform', OTHER: 'Document' };
const HOUR_MS = 60 * 60 * 1000;

const statusAgeHours = (detail) => {
  const enteredAt = new Date(detail.statusEnteredAt || detail.disputeDetails?.raisedAt).getTime();
  return Number.isFinite(enteredAt) ? Math.max(0, (Date.now() - enteredAt) / HOUR_MS) : 0;
};

const shortDuration = (hours) => hours < 24 ? `${Math.max(0, Math.floor(hours))}h` : `${(hours / 24).toFixed(1)}d`;

const taskMeta = (detail) => {
  const age = statusAgeHours(detail);
  if (detail.status === 'DISPUTE_PENDING') {
    const remaining = 24 - age;
    return { kicker: 'Raise on platform', title: `Create the dispute on ${detail.platform}`, timing: remaining > 0 ? `Due in ${shortDuration(remaining)}` : `${shortDuration(Math.abs(remaining))} overdue`, tone: remaining > 0 ? 'normal' : 'overdue' };
  }
  if (detail.status === 'QUEUED_FOR_UPDATE') {
    const remaining = 24 - age;
    return { kicker: 'Submit brand response', title: `Post the response on ${detail.platform}`, timing: remaining > 0 ? `Due in ${shortDuration(remaining)}` : `${shortDuration(Math.abs(remaining))} overdue`, tone: remaining > 0 ? 'normal' : 'overdue' };
  }
  if (detail.status === 'AWAITING_PLATFORM_RESPONSE') {
    const remaining = 48 - age;
    return age >= 48
      ? { kicker: 'Platform follow-up due', title: `Check and follow up with ${detail.platform}`, timing: `${shortDuration(age)} in status`, tone: 'overdue' }
      : { kicker: 'Waiting on platform', title: `Monitor ${detail.platform} for a response`, timing: `Follow up in ${shortDuration(remaining)}`, tone: 'waiting' };
  }
  if (detail.status === 'AWAITING_BRAND_RESPONSE') return { kicker: 'Waiting on brand', title: 'No ops action required right now', timing: `${shortDuration(age)} in status`, tone: 'waiting' };
  return { kicker: 'Completed', title: 'This dispute is closed', timing: 'Closed', tone: 'done' };
};

function CollapsibleSection({ icon: Icon, title, count, defaultOpen = false, children }) {
  return (
    <details className="disp-collapse" open={defaultOpen || undefined}>
      <summary><span><Icon size={13} /> {title}</span><span className="disp-collapse-count">{count}</span><ChevronDown size={14} className="disp-collapse-arrow" /></summary>
      <div className="disp-collapse-body">{children}</div>
    </details>
  );
}

function EvidenceItem({ item, onView, onDownload, downloading }) {
  if (item.kind === 'poNumber') {
    return (
      <div className="disp-ev-item">
        <div className="disp-ev-ic"><Hash size={14} /></div>
        <span className="disp-ev-n disp-mono">{item.value}</span>
        <span className="disp-ev-meta">PO Number</span>
      </div>
    );
  }
  const Icon = DOC_ICON[item.docType] || FileText;
  return (
    <div className="disp-ev-item">
      <div className="disp-ev-ic"><Icon size={14} /></div>
      <span className="disp-ev-n">{item.filename}</span>
      {item.isNew && <span className="disp-ev-new">NEW</span>}
      <span className="disp-ev-meta">{[DOC_LABEL[item.docType], item.sizeLabel].filter(Boolean).join(' · ')}</span>
      <button className="disp-ev-dl" onClick={() => onView(item)} title="View"><Eye size={13} /></button>
      <button className="disp-ev-dl" onClick={() => onDownload(item.id)} disabled={downloading === item.id} title="Download">
        {downloading === item.id ? <Loader2 size={13} className="disp-spin" /> : <Download size={13} />}
      </button>
    </div>
  );
}

function ThreadMessage({ m, platform, brand, onDownload, downloading }) {
  const isBrand = m.represents === 'BRAND';
  const isSystem = m.represents === 'SYSTEM';
  const messageClass = isBrand ? 'disp-om-brand' : isSystem ? 'disp-om-system' : 'disp-om-platform';
  const avatar = isBrand ? brandInitials(brand) : isSystem ? 'AR' : (platform || '??').slice(0, 2).toUpperCase();
  const name = isBrand ? brand : isSystem ? 'ARPFlow' : `${platform} Support`;
  return (
    <div className={`disp-om ${messageClass}`}>
      <div className="disp-om-av" style={{ background: isBrand ? brandColor(brand) : isSystem ? 'var(--purple)' : 'var(--accent)' }}>
        {avatar}
      </div>
      <div className="disp-om-body">
        <div className="disp-om-meta">
          <span className="disp-om-name">{name}</span>
          <span className="disp-om-time">{fmtDateTime(m.createdAt)}</span>
        </div>
        <div className="disp-om-bubble">
          {m.body && <div>{m.body}</div>}
          {(m.attachments || []).length > 0 && (
            <div className="disp-chat-files">
              {m.attachments.map(file => (
                <button key={file.id} onClick={() => onDownload(file.id)} disabled={downloading === file.id}>
                  {downloading === file.id ? <Loader2 size={12} className="disp-spin" /> : <Paperclip size={12} />}
                  <span>{file.filename}</span>{file.sizeLabel && <small>{file.sizeLabel}</small>}
                </button>
              ))}
            </div>
          )}
          {m.deliveryState && <span className={`disp-delivery ${m.deliveryState.toLowerCase()}`}>{m.deliveryState === 'POSTED' ? 'Posted on platform' : 'Queued for platform submission'}</span>}
        </div>
      </div>
    </div>
  );
}

function ActivityItem({ activity }) {
  return (
    <div className={`disp-activity ${activity.importance === 'prominent' ? 'prominent' : ''} ${activity.tone || 'neutral'}`}>
      <Zap size={12} />
      <div><strong>{activity.title}</strong>{activity.description && <span>{activity.description}</span>}</div>
      <time>{fmtDateTime(activity.createdAt)}</time>
    </div>
  );
}

// Same labels ARP's invoice drawer uses for the invoice's own platform docs.
const PLATFORM_DOC_LABELS = {
  discrepancy: 'Discrepancy Note',
  grn: 'GRN Copy',
  invoice: 'Invoice PDF',
  pod: 'POD',
  scanned_invoice: 'POD',
};

// A row without sourceFlag comes from a backend that only sent DN docs.
const isInvoiceDoc = (doc) => doc.sourceFlag === 'invoice_related';

const platformDocLabel = (doc) => {
  if (!isInvoiceDoc(doc)) return doc.debitNoteNumber || 'DN (number unavailable)';
  const type = String(doc.docType || '');
  return PLATFORM_DOC_LABELS[type.toLowerCase()] ?? (type.charAt(0).toUpperCase() + type.slice(1));
};

// Ids repeat across platform_invoice_doc, dn_documents and the evidence docs
// that share downloadingDocId; uuid does not.
const platformDocKey = (doc) => doc.uuid ?? `dn-${doc.id}`;

function PlatformDocRow({ doc, onView, onDownload, downloading }) {
  const linkLabel = { ref_invoice: 'Direct', adjusted_invoice: 'Adjusted' };
  const busy = downloading === platformDocKey(doc);
  return (
    <div className="disp-ev-item">
      <div className="disp-ev-ic"><FileText size={14} /></div>
      <span className={`disp-ev-n${isInvoiceDoc(doc) ? '' : ' disp-mono'}`}>{platformDocLabel(doc)}</span>
      <span className="disp-ev-meta">
        {[doc.dnType, linkLabel[doc.linkType] || doc.linkType].filter(Boolean).join(' · ')}
      </span>
      <button className="disp-ev-dl" onClick={() => onView(doc)} title="View"><Eye size={13} /></button>
      <button className="disp-ev-dl" onClick={() => onDownload(doc)} disabled={busy} title="Download">
        {busy ? <Loader2 size={13} className="disp-spin" /> : <Download size={13} />}
      </button>
    </div>
  );
}

// Platform docs already pulled for this invoice — its own copies and its
// debit notes, split the way ARP's invoice drawer splits them. Reference
// material, NOT submitted evidence — the header says so explicitly so nobody
// mistakes these for documents the brand filed with the dispute.
export function DnDocuments({ detail, onView, onDownload, downloading }) {
  const docs = detail.dnDocuments || [];
  if (!docs.length) return null;

  const groups = [
    { title: 'Invoice documents', docs: docs.filter(isInvoiceDoc) },
    { title: 'Debit notes', docs: docs.filter(doc => !isInvoiceDoc(doc)) },
  ].filter(group => group.docs.length);

  return (
    <section className="disp-case-references" aria-labelledby="dn-docs-title">
      <div className="disp-case-references-head">
        <FileText size={14} />
        <div>
          <strong id="dn-docs-title">Documents from platform</strong>
          <span>Reference only — not part of submitted evidence</span>
        </div>
      </div>
      {groups.map(group => (
        <div className="disp-case-references-block" key={group.title}>
          <p className="disp-case-references-group">{group.title}</p>
          <div className="disp-ev">
            {group.docs.map(doc => (
              <PlatformDocRow key={platformDocKey(doc)} doc={doc} onView={onView} onDownload={onDownload} downloading={downloading} />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

function CaseReferences({ detail }) {
  const { poNumber, poNumberSource, grnNumber, grnNumberSource } = detail.disputeDetails || {};
  const references = [
    poNumber && {
      key: 'po',
      label: 'PO Number',
      value: poNumber,
      source: poNumberSource === 'BRAND' ? 'Shared by the brand' : 'As per GRN Report',
    },
    grnNumber && {
      key: 'grn',
      label: 'GRN Number',
      value: grnNumber,
      source: grnNumberSource === 'GRN_REPORT' ? 'As per GRN Report' : undefined,
    },
  ].filter(Boolean);

  if (!references.length) return null;

  return (
    <section className="disp-case-references" aria-label="References for working this dispute">
      <div className="disp-case-references-head">
        <Hash size={14} />
        <div><strong>References to use on the portal</strong><span>Verify these while raising or updating the ticket</span></div>
      </div>
      <div className="disp-case-reference-grid">
        {references.map(reference => (
          <div key={reference.key} className="disp-case-reference">
            <span>{reference.label}</span>
            <strong>{reference.value}</strong>
            {reference.source && <small>{reference.source}</small>}
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Contextual action zone — one shape per dispute status ────────────────────
// The KAM never asserts a win/lose outcome here — closing just means the
// platform said the claim is settled. The real outcome is only confirmed
// later, automatically, once reconciliation data (the next GRN/CN excel)
// confirms it. So this is a single "Close" action with an optional note,
// not a won/lost choice.
function CloseRow({ busy, onAction }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const canClose = reason && (reason !== 'Other' || details.trim());

  if (!open) {
    return <Button variant="ghost" size="sm" disabled={busy} onClick={() => setOpen(true)}>More actions <ChevronDown size={12} /></Button>;
  }

  return (
    <div className="disp-close-guard">
      <div className="disp-close-warning"><AlertTriangle size={14} /><span><strong>Close this dispute?</strong>This also closes the client’s action ticket and notifies the brand.</span></div>
      <div className="disp-field">
        <label>Closure reason</label>
        <select className="disp-input" value={reason} onChange={e => setReason(e.target.value)}>
          <option value="">Select a reason</option>
          <option>Platform confirmed the case is settled</option>
          <option>Resolved outside the dispute with a GRN/CN</option>
          <option>Dispute is no longer required</option>
          <option>Other</option>
        </select>
      </div>
      <div className="disp-field">
        <label>{reason === 'Other' ? 'Details' : 'Additional details (optional)'}</label>
        <input
          className="disp-input"
          placeholder="Add the supporting context for the audit trail"
          value={details}
          onChange={e => setDetails(e.target.value)}
        />
      </div>
      <div className="disp-close-actions">
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => { setOpen(false); setReason(''); setDetails(''); }}>Cancel</Button>
        <Button variant="destructive" size="sm" disabled={!canClose || busy} onClick={() => onAction('resolveDispute', { text: `${reason}${details.trim() ? ` — ${details.trim()}` : ''}` })}><Check size={12} /> Confirm close</Button>
      </div>
    </div>
  );
}

function UpdateTicketNumberControl({ detail, busy, onAction }) {
  const [open, setOpen] = useState(false);
  const [ticketNo, setTicketNo] = useState(detail.disputeDetails?.platformTicketNumber || '');

  useEffect(() => {
    setTicketNo(detail.disputeDetails?.platformTicketNumber || '');
    setOpen(false);
  }, [detail.disputeId, detail.disputeDetails?.platformTicketNumber]);

  if (!open) {
    return (
      <button className="disp-override-toggle" onClick={() => setOpen(true)}>
        <RotateCcw size={11} /> Update ticket number
      </button>
    );
  }

  return (
    <div className="disp-override-box">
      <div className="disp-field">
        <label>Platform ticket / case number</label>
        <input
          className="disp-input"
          placeholder="e.g. 6740000"
          value={ticketNo}
          onChange={e => setTicketNo(e.target.value)}
        />
      </div>
      <div className="disp-btn-row">
        <Button variant="outline" size="sm" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
        <Button
          size="sm"
          disabled={busy || !ticketNo.trim() || ticketNo.trim() === detail.disputeDetails?.platformTicketNumber}
          onClick={() => onAction('updateTicketNumber', { ticketNo: ticketNo.trim() }, () => setOpen(false))}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

function ActionZone({ detail, busy, onAction, onDownload, downloading }) {
  const [ticketNo, setTicketNo] = useState('');
  const [platReply, setPlatReply] = useState('');
  const [platFiles, setPlatFiles] = useState([]);
  const [copied, setCopied] = useState(false);
  const [postedConfirmed, setPostedConfirmed] = useState(false);
  useEffect(() => { setTicketNo(''); setPlatReply(''); setCopied(false); setPostedConfirmed(false); }, [detail.disputeId, detail.status]);

  const status = detail.status;
  const ticketNumber = detail.disputeDetails?.platformTicketNumber;

  if (status === 'DISPUTE_PENDING') {
    return (
      <div className="disp-task-form">
        <p>Open the submitted evidence below, raise the dispute on the portal, then record the platform’s ticket number.</p>
        <div className="disp-field">
          <label>Platform ticket / case number</label>
          <input className="disp-input" placeholder="e.g. 6740000" value={ticketNo} onChange={e => setTicketNo(e.target.value)} />
        </div>
        <Button disabled={!ticketNo.trim() || busy} onClick={() => onAction('markFiledOnPortal', { ticketNo: ticketNo.trim() })}>
          <ExternalLink size={13} /> Confirm ticket raised
        </Button>
      </div>
    );
  }

  if (status === 'AWAITING_PLATFORM_RESPONSE') {
    const enteredAt = new Date(detail.statusEnteredAt || detail.disputeDetails?.raisedAt).getTime();
    const ageDays = Number.isFinite(enteredAt) ? Math.max(0, (Date.now() - enteredAt) / (24 * 60 * 60 * 1000)) : 0;
    const followUpDue = ageDays >= 2;
    return (
      <div className="disp-task-form">
        <p>{followUpDue ? `Check ticket ${ticketNumber || ''} on ${detail.platform} and follow up if there is still no response.` : `No follow-up is needed yet. When ${detail.platform} responds, record it here.`}</p>
        <div className="disp-field">
          <label>Platform response</label>
          <textarea className="disp-ta" placeholder="Paste the platform’s response…" value={platReply} onChange={e => setPlatReply(e.target.value)} />
        </div>
        <div className="disp-field">
          <label>Documents from the platform (optional)</label>
          <input
            type="file"
            multiple
            className="disp-input"
            accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx,.csv"
            onChange={e => setPlatFiles(Array.from(e.target.files || []))}
          />
          {platFiles.length > 0 && (
            <small className="disp-muted">{platFiles.map(f => f.name).join(', ')}</small>
          )}
        </div>
        <Button
          disabled={!platReply.trim() || busy}
          onClick={() => onAction('logPlatformReply', { text: platReply.trim() }, () => setPlatFiles([]), platFiles)}
        ><Send size={13} /> Send response to brand</Button>
        <div className="disp-secondary-actions"><CloseRow busy={busy} onAction={onAction} /></div>
      </div>
    );
  }

  if (status === 'AWAITING_BRAND_RESPONSE') {
    return (
      <div className="disp-task-wait">
        <Clock size={16} />
        <div><strong>The brand has the platform’s response.</strong><span>When they reply, this case will automatically move to “Submit brand responses.”</span></div>
        <div className="disp-secondary-actions"><CloseRow busy={busy} onAction={onAction} /></div>
      </div>
    );
  }

  if (status === 'QUEUED_FOR_UPDATE') {
    const lastBrandMsg = [...(detail.messages || [])].reverse().find(m => m.represents === 'BRAND');
    return (
      <div className="disp-task-form disp-task-submit">
          <p>Post this complete response on ticket {ticketNumber || 'on the platform'}, including every attachment.</p>
          {lastBrandMsg && (
            <div className="disp-reply">
              <div className="disp-reply-h">
                <span className="disp-reply-t">Brand's reply · {fmtDateTime(lastBrandMsg.createdAt)}</span>
                <button
                  className={`disp-copy ${copied ? 'done' : ''}`}
                  onClick={() => { navigator.clipboard?.writeText(lastBrandMsg.body); setCopied(true); }}
                >
                  {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy text</>}
                </button>
              </div>
              <div className="disp-reply-body">{lastBrandMsg.body}</div>
              {(lastBrandMsg.attachments || []).length > 0 && (
                <div className="disp-reply-files">
                  <strong><Paperclip size={12} /> {lastBrandMsg.attachments.length} attachment{lastBrandMsg.attachments.length === 1 ? '' : 's'} must also be posted</strong>
                  {lastBrandMsg.attachments.map(file => (
                    <button key={file.id} onClick={() => onDownload(file.id)} disabled={downloading === file.id}>
                      {downloading === file.id ? <Loader2 size={12} className="disp-spin" /> : <Download size={12} />}
                      <span>{file.filename}</span>{file.sizeLabel && <small>{file.sizeLabel}</small>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <label className="disp-confirm-check"><input type="checkbox" checked={postedConfirmed} onChange={e => setPostedConfirmed(e.target.checked)} /><span>I posted the complete brand response{(lastBrandMsg?.attachments || []).length ? ' and every attachment' : ''} on the platform portal.</span></label>
          <Button disabled={busy || !postedConfirmed} onClick={() => onAction('confirmPostedOnPortal', {})}><Check size={13} /> Mark as posted on portal</Button>
          <div className="disp-secondary-actions"><CloseRow busy={busy} onAction={onAction} /></div>
      </div>
    );
  }

  // DONE — no win/lose framing here; the actual outcome is only confirmed
  // later, automatically, once reconciliation data confirms it.
  return (
    <div className="disp-done-note closed">
      <Check size={15} />
      Marked as closed. Brand notified.
    </div>
  );
}

export default function DisputeOpsPanel({ disputeId, cfg, workQueueIds = [], onSelectDispute, onClose, onChanged }) {
  const open = !!disputeId;
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [downloadingDocId, setDownloadingDocId] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [viewingDocUrl, setViewingDocUrl] = useState(null);
  const [viewingDocLoading, setViewingDocLoading] = useState(false);

  useEffect(() => {
    if (!disputeId || !cfg) { setDetail(null); return; }
    let cancelled = false;
    setDetail(null);
    setError(null);
    setLoading(true);
    fetchDisputeDetail(BACKEND_BASE_URL, cfg, disputeId)
      .then(data => { if (!cancelled) setDetail(data); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [disputeId, cfg]);

  useEffect(() => { setNote(''); setNoteOpen(false); }, [disputeId]);

  const runAction = async (action, payload, onSuccess, attachments = []) => {
    if (!disputeId) return;
    setBusy(true);
    setError(null);
    try {
      await runDisputeOpsAction(BACKEND_BASE_URL, cfg, disputeId, action, payload, attachments);
      const fresh = await fetchDisputeDetail(BACKEND_BASE_URL, cfg, disputeId);
      setDetail(fresh);
      onSuccess?.();
      onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async (docId) => {
    setDownloadingDocId(docId);
    try {
      const res = await fetchDocumentUrl(BACKEND_BASE_URL, cfg, docId);
      if (res?.url) window.open(res.url, '_blank');
      else setError('Could not retrieve download URL.');
    } catch (e) {
      setError(e.message);
    } finally {
      setDownloadingDocId(null);
    }
  };

  const handleView = async (item) => {
    setViewingDoc(item);
    setViewingDocUrl(null);
    setViewingDocLoading(true);
    try {
      const res = await fetchDocumentUrl(BACKEND_BASE_URL, cfg, item.id);
      if (res?.url) setViewingDocUrl(res.url);
      else {
        setError('Could not retrieve document URL.');
        setViewingDoc(null);
      }
    } catch (e) {
      setError(e.message);
      setViewingDoc(null);
    } finally {
      setViewingDocLoading(false);
    }
  };

  // Platform docs go through the dn router (own bucket, needs docSource)
  // rather than /api/actions/RedirectToDoc, so they get their own pair of
  // handlers instead of overloading the evidence/chat ones.
  const handleDnDownload = async (doc) => {
    setDownloadingDocId(platformDocKey(doc));
    try {
      const res = await fetchDnDocumentUrl(BACKEND_BASE_URL, cfg, doc.id, doc.docSource, doc.clientId);
      if (res?.url) window.open(res.url, '_blank');
      else setError('This platform document is not available.');
    } catch (e) {
      setError(e.message);
    } finally {
      setDownloadingDocId(null);
    }
  };

  const handleDnView = async (doc) => {
    setViewingDoc({ ...doc, filename: platformDocLabel(doc) });
    setViewingDocUrl(null);
    setViewingDocLoading(true);
    try {
      const res = await fetchDnDocumentUrl(BACKEND_BASE_URL, cfg, doc.id, doc.docSource, doc.clientId);
      if (res?.url) setViewingDocUrl(res.url);
      else {
        setError('This platform document is not available.');
        setViewingDoc(null);
      }
    } catch (e) {
      setError(e.message);
      setViewingDoc(null);
    } finally {
      setViewingDocLoading(false);
    }
  };

  const timeline = detail ? [
    ...(detail.messages || []).map(item => ({ kind: 'message', item })),
    ...(detail.activities || []).map(item => ({ kind: 'activity', item })),
  ].sort((a, b) => new Date(a.item.createdAt) - new Date(b.item.createdAt)) : [];
  const currentTask = detail ? taskMeta(detail) : null;
  const queueIndex = workQueueIds.indexOf(disputeId);
  const hasQueueNavigation = queueIndex >= 0 && workQueueIds.length > 1;

  return (
    <>
      <div className={`disp-scrim ${open ? 'on' : ''}`} onClick={onClose} />
      <aside className={`disp-panel ${open ? 'on' : ''}`}>
        {loading && <div className="disp-panel-loading"><Loader2 size={20} className="disp-spin" /> Loading dispute…</div>}

        {!loading && detail && (
          <>
            <div className="disp-panel-head">
              <div className="disp-panel-head-top">
                <div className="disp-panel-brand">
                  <span className="disp-b-av" style={{ background: brandColor(detail.brand) }}>{brandInitials(detail.brand)}</span>
                  <span>{detail.brand}<small>{detail.platform} portal</small></span>
                </div>
                <div className="disp-panel-tools">
                  {hasQueueNavigation && (
                    <div className="disp-ticket-nav" aria-label="Work queue navigation">
                      <button disabled={queueIndex === 0} onClick={() => onSelectDispute?.(workQueueIds[queueIndex - 1])} aria-label="Previous ticket"><ChevronLeft size={15} /></button>
                      <span>{queueIndex + 1} of {workQueueIds.length}</span>
                      <button disabled={queueIndex === workQueueIds.length - 1} onClick={() => onSelectDispute?.(workQueueIds[queueIndex + 1])} aria-label="Next ticket"><ChevronRight size={15} /></button>
                    </div>
                  )}
                  <button className="disp-panel-close" onClick={onClose} aria-label="Close"><X size={17} /></button>
                </div>
              </div>
              <div className="disp-task-headline">
                <div><span className={`disp-task-kicker ${currentTask.tone}`}>{currentTask.kicker}</span><h3>{currentTask.title}</h3></div>
                <span className={`disp-task-timing ${currentTask.tone}`}><Clock size={11} /> {currentTask.timing}</span>
              </div>
              <div className="disp-case-line">
                <span><small>Invoice</small>{detail.header?.invoiceNumber}</span>
                <span><small>Amount</small>{fmtFullINR(detail.disputeDetails?.invoiceValue)}</span>
                <span><small>Issue</small>{formatIssueType(detail.disputeDetails?.issueType)}</span>
                {detail.disputeDetails?.platformTicketNumber && <span><small>Ticket</small>{detail.disputeDetails.platformTicketNumber}</span>}
              </div>
            </div>

            <div className="disp-panel-body">
              {error && <p className="disp-panel-error">{error}</p>}

              <CaseReferences detail={detail} />
              <DnDocuments
                detail={detail}
                onView={handleDnView}
                onDownload={handleDnDownload}
                downloading={downloadingDocId}
              />

              <ActionZone
                detail={detail}
                busy={busy}
                onAction={(action, payload, onSuccess, attachments) => runAction(action, payload, onSuccess, attachments)}
                onDownload={handleDownload}
                downloading={downloadingDocId}
              />
              <UpdateTicketNumberControl
                detail={detail}
                busy={busy}
                onAction={(action, payload, onSuccess) => runAction(action, payload, onSuccess)}
              />

              <div className="disp-supporting">
                {detail.disputeDetails?.notes && (
                  <div className="disp-brand-submission-note">
                    <div><MessageSquare size={14} /><strong>Brand submission note</strong></div>
                    <p>{detail.disputeDetails.notes}</p>
                  </div>
                )}
                <CollapsibleSection icon={FileText} title="Submitted evidence" count={(detail.evidence || []).length} defaultOpen={detail.status === 'DISPUTE_PENDING'}>
                  <div className="disp-ev">{(detail.evidence || []).map((item, i) => <EvidenceItem key={i} item={item} onView={handleView} onDownload={handleDownload} downloading={downloadingDocId} />)}</div>
                </CollapsibleSection>
                <CollapsibleSection icon={MessageSquare} title="Conversation and activity" count={timeline.length}>
                  <div className="disp-thread">{timeline.map(entry => entry.kind === 'message'
                    ? <ThreadMessage key={`m-${entry.item.id}`} m={entry.item} platform={detail.platform} brand={detail.brand} onDownload={handleDownload} downloading={downloadingDocId} />
                    : <ActivityItem key={`a-${entry.item.id}`} activity={entry.item} />)}</div>
                </CollapsibleSection>

                {detail.status !== 'DONE' && (
                  <div className="disp-note-collapsed">
                    {!noteOpen ? (
                      <button onClick={() => setNoteOpen(true)}><Zap size={13} /><span><strong>Send an ARPFlow update</strong><small>Visible to the brand</small></span></button>
                    ) : (
                      <div className="disp-note-box">
                        <textarea placeholder="Tell the brand what information or evidence is needed…" value={note} onChange={e => setNote(e.target.value)} />
                        <div className="disp-note-actions">
                          <Button variant="ghost" size="sm" onClick={() => { setNoteOpen(false); setNote(''); }}>Cancel</Button>
                          <Button size="sm" disabled={!note.trim() || busy} onClick={() => runAction('addNote', { text: note.trim() }, () => { setNote(''); setNoteOpen(false); })}>Send update</Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </aside>

      <Dialog open={!!viewingDoc} onOpenChange={(nextOpen) => { if (!nextOpen) { setViewingDoc(null); setViewingDocUrl(null); } }}>
        <DialogContent className="disp-doc-preview">
          <DialogHeader>
            <DialogTitle>{viewingDoc?.filename ?? 'Document'}</DialogTitle>
          </DialogHeader>
          <div className="disp-doc-preview-body">
            {viewingDocLoading && <div className="disp-panel-loading"><Loader2 size={20} className="disp-spin" /> Loading document…</div>}
            {!viewingDocLoading && viewingDocUrl && <iframe src={viewingDocUrl} title={viewingDoc?.filename ?? 'Document'} />}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
